import type { EditRequestStatus } from '#core/layers/auth/utils/project-management'
import { useDevLogger } from '#core/composables/useDevLogger'
import { useAuth } from '#core/layers/auth/composables/useAuth'
import { evaluateEditRequestStatus } from '#core/layers/auth/utils/project-management'
import { useMutation, useQuery } from '@pinia/colada'
import { defineStore } from 'pinia'

type ProjectEditorState = SuccessResponseBody<'/project/{projectId}/editors'>
type ProjectEditorUser = ProjectEditorState['activeEditor']

export const useProjectEditors = defineStore('projectEditors', () => {
  const logger = useDevLogger('useProjectEditors')
  const auth = useAuth()

  const editRequestStatus = ref<EditRequestStatus>('none')

  // Core data
  const canRequestEdit = ref(false)
  const autoApproveAt = ref<Date | null>(null)
  const activeEditor = ref<ProjectEditorUser>()
  const pendingEditor = ref<ProjectEditorUser>()
  const isActiveEditor = computed(() => activeEditor.value?.id === auth.me?.id)
  const autoApproveIntervalSeconds = ref<number>()
  const isInitialized = ref(false)

  const { mutateAsync: requestToEditProject, isLoading: isRequesting, error: requestToEditError } = useMutation(buildApiMutation('/project/{projectId}/permissions/request', {
    method: 'post',
  }))

  const { mutateAsync: approveEditRequest, isLoading: isApproving, error: approveEditRequestError } = useMutation(buildApiMutation('/project/{projectId}/permissions/approve', {
    method: 'post',
  }))

  const { mutateAsync: rejectEditRequest, isLoading: isRejecting, error: rejectEditRequestError } = useMutation(buildApiMutation('/project/{projectId}/permissions/reject', {
    method: 'post',
  }))

  const isLoading = computed(() => isRequesting.value || isApproving.value || isRejecting.value)
  const error = computed(() => requestToEditError.value || approveEditRequestError.value || rejectEditRequestError.value)

  const { refetch: refetchProjectEditors } = useQuery({
    ...buildApiQuery('/project/{projectId}/editors', {
      params: () => ({
        path: {
          projectId: auth.selectedProjectId!,
        },
      }),
    }),
    enabled: false,
  })

  // Auto-initialize data when project changes - only on client
  // This watch triggers API calls that require auth, which isn't available during SSR
  if (import.meta.client) {
    watch([() => auth.selectedProjectId, () => auth.me?.id], async (projectId) => {
      if (!projectId) {
        reset()
        return
      }

      // Wait for any ongoing auth operations to complete before making API calls
      // This prevents race conditions during session transitions (e.g., email change)
      await auth.waitForAuth({ from: 'useProjectEditors' })
      await initializeFromApi()
    }, { immediate: true })
  }

  // Actions
  async function request() {
    if (!auth.selectedProjectId) {
      return
    }
    await requestToEditProject({
      path: { projectId: auth.selectedProjectId },
    })
    editRequestStatus.value = 'none'
  }

  async function approve() {
    if (!auth.selectedProjectId) {
      return
    }

    await approveEditRequest({
      path: { projectId: auth.selectedProjectId },
    })
    editRequestStatus.value = 'none'
  }

  async function reject() {
    if (!auth.selectedProjectId) {
      return
    }

    await rejectEditRequest({
      path: { projectId: auth.selectedProjectId },
    })
    editRequestStatus.value = 'none'
  }

  function updateEditorData(data: ProjectEditorState) {
    activeEditor.value = data.activeEditor
    pendingEditor.value = data.pendingEditor ?? undefined
    canRequestEdit.value = data.canRequestEdit
    autoApproveAt.value = data.autoApproveAt ? new Date(data.autoApproveAt) : null
    autoApproveIntervalSeconds.value = data.autoApproveIntervalSeconds
    logger.debug('[updateEditorData] Updated editor data', data)
  }

  function setEditRequestStatus(status: EditRequestStatus) {
    editRequestStatus.value = status
    logger.debug('[setEditRequestStatus] Edit request status', { status })
  }

  function setPendingEditor(editor: ProjectEditorUser | undefined) {
    pendingEditor.value = editor
    logger.debug('[setPendingEditor] Pending editor', { editor })
  }

  function setActiveEditor(editor: ProjectEditorUser) {
    activeEditor.value = editor
    logger.debug('[setActiveEditor] Active editor', { editor })
  }

  function clearPendingEditor() {
    pendingEditor.value = undefined
    logger.debug('[clearPendingEditor] Cleared pending editor')
  }

  async function initializeFromApi() {
    logger.debug('[initializeFromApi] Initializing project editors data from API')
    if (!auth.selectedProjectId || !auth.me?.id) {
      return
    }
    // Snapshot current state BEFORE refresh
    const prevData = activeEditor.value
      ? {
          activeEditor: activeEditor.value,
          pendingEditor: pendingEditor.value ?? null,
          canRequestEdit: canRequestEdit.value,
          isActiveEditor: isActiveEditor.value,
          autoApproveAt: autoApproveAt.value?.toISOString() ?? null,
          autoApproveIntervalSeconds: autoApproveIntervalSeconds.value ?? 0,
        }
      : undefined

    const { data } = await refetchProjectEditors()
    if (!data) {
      return
    }
    // Evaluate status against previous state (if any)
    if (prevData) {
      editRequestStatus.value = evaluateEditRequestStatus(prevData, data, auth.me.id)
    }
    else if (data.pendingEditor && data.activeEditor.id === auth.me.id) {
      // On first load: if I'm the active editor and there's a pending request
      editRequestStatus.value = 'pending_request'
    }
    // Finally, apply the fresh data
    updateEditorData(data)
    isInitialized.value = true
  }

  // Reset data
  function reset() {
    activeEditor.value = undefined
    pendingEditor.value = undefined
    editRequestStatus.value = 'none'
    isInitialized.value = false
    logger.debug('[reset] Reset editor data')
  }

  return {
    isLoading,
    error,
    editRequestStatus,

    isActiveEditor,
    canRequestEdit,
    activeEditor,
    pendingEditor,
    autoApproveAt,
    autoApproveIntervalSeconds,
    isInitialized,

    request,
    approve,
    reject,
    refetch: initializeFromApi,
    initializeFromApi,
    reset,

    updateEditorData,
    setEditRequestStatus,
    setPendingEditor,
    setActiveEditor,
    clearPendingEditor,
  }
})
